import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import useMqttApi from "hooks/use-mqtt-api";
import { Color } from "utils/theme";
import { Captain } from "types/captains";
import { RequestTopics } from "services/topics";
import { useStore } from "hooks/use-store";
import { Screens } from "navigation/routes";
import { Button } from "components/ui/button";
import { ScrollButtons } from "components/ui/scroll-buttons";
import { CaptainList } from "./components/captain-list";

export const CaptainSelection = () => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const config = useStore((state) => state.config);
  const setScreen = useStore((state) => state.setScreen);

  // Determine if captain selection is required
  const isRequired = config?.company_settings?.force_captain_login;

  // Current trip captain
  const tripCaptain = useStore((state) => state.tripCaptain);
  const setStoreTripCaptain = useStore((state) => state.setTripCaptain);

  // Local state for selected captain
  const [selectedCaptain, setSelectedCaptain] = useState<Captain | null>(
    tripCaptain ?? null
  );

  // Captain of the in-flight update request; a ref because the MQTT
  // onSuccess callback runs in a stale closure and can't read state
  const pendingCaptainRef = useRef<Captain | null>(null);

  // Update selected captain when trip captain changes
  useEffect(() => {
    if (tripCaptain) {
      setSelectedCaptain(tripCaptain);
    }
  }, [tripCaptain]);

  // MQTT API hook for setting trip captain
  const { fetch: setTripCaptain, loading } = useMqttApi({
    requestTopic: RequestTopics.TripCaptain,
    enabled: false,
    onError: (error) => toast.error(error.message),
    onSuccess: () => {
      // Update the store immediately instead of waiting for the backend
      // to echo on the trip_captain topic
      setStoreTripCaptain(pendingCaptainRef.current);
      setScreen(Screens.Dashboard);
    },
  });

  const onUpdateCaptain = () => {
    // Ensure a captain is selected if required
    if (!selectedCaptain && isRequired) {
      toast.error(t("captain_selection.captain_mandatory_message"));
      return;
    }

    pendingCaptainRef.current = selectedCaptain;

    setTripCaptain({
      selected_captain: {
        id: selectedCaptain ? selectedCaptain.id : null,
      },
    });
  };

  // Show cancel button only if selection is not required or no captains are available
  const showCancelButton = !isRequired || !config.captains?.length;

  return (
    <section
      className="h-full w-full overflow-hidden overflow-y-auto"
      style={{ background: Color.Primary[900] }}
      ref={scrollRef}
    >
      <header
        className="sticky top-0 w-full flex items-center justify-between py-3 px-4 border-b"
        style={{
          borderColor: Color.Primary[600],
          background: Color.Primary[900],
        }}
      >
        <h2 className="text-xl font-medium">
          {t("captain_selection.select_captain")}
        </h2>
        <div className="flex items-center space-x-2">
          {showCancelButton && (
            <Button
              variant="secondary"
              onClick={() => {
                setScreen(Screens.Dashboard);
              }}
            >
              {t("common.cancel")}
            </Button>
          )}
          <Button onClick={() => onUpdateCaptain()} disabled={loading}>
            {loading && (
              <Loader2
                className={"animate-spin size-5 mr-2"}
                stroke={Color.White}
              />
            )}
            {t("captain_selection.update_captain")}
          </Button>
        </div>
      </header>
      <CaptainList
        captains={config?.captains ?? []}
        value={selectedCaptain}
        onValueChange={setSelectedCaptain}
      />
      <div className="absolute z-10 bottom-6 right-6">
        <ScrollButtons containerRef={scrollRef} />
      </div>
    </section>
  );
};
